import {RefObject, useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {CheckVerifiedDomainStatus} from "@evennt-tech/api-v2-external";
import {selectUser} from "store/auth/selectors";
import {extractOrigin} from "tools/url/extractDomain";
import {useMyVerifiedDomains} from "hooks/verifiedDomain/useMyVerifiedDomains";
import {useCheckVerifiedDomains} from "hooks/verifiedDomain/useCheckVerifiedDomains";
import {Roles} from "@evennt-tech/api-v2-core";

export type ReadonlyOverlayStatus =
  | CheckVerifiedDomainStatus.PENDING
  | CheckVerifiedDomainStatus.REJECTED;

export type ReadonlyOverlayEntry = {
  wrap: HTMLElement;
  status: ReadonlyOverlayStatus;
};

const IFRAME_CLASS = "ffair-iframe";
const IFRAME_WRAP_CLASS = "ffair-iframe-wrap";

const originalWrapStyles = new WeakMap<HTMLElement, string | null>();
const originalIframeStyles = new WeakMap<HTMLIFrameElement, string | null>();

const restoreStyle = (node: HTMLElement, style: string | null) => {
  if (style === null) {
    node.removeAttribute("style");
    return;
  }
  node.setAttribute("style", style);
};

const restoreIframeVisibility = (root: HTMLElement) => {
  root
    .querySelectorAll<HTMLElement>(`.${IFRAME_WRAP_CLASS}`)
    .forEach((wrap) => {
      if (!originalWrapStyles.has(wrap)) return;
      restoreStyle(wrap, originalWrapStyles.get(wrap) ?? null);
      originalWrapStyles.delete(wrap);
    });

  root
    .querySelectorAll<HTMLIFrameElement>(`iframe.${IFRAME_CLASS}`)
    .forEach((node) => {
      if (originalIframeStyles.has(node)) {
        restoreStyle(node, originalIframeStyles.get(node) ?? null);
        originalIframeStyles.delete(node);
      } else {
        node.style.removeProperty("visibility");
      }

      if (!node.getAttribute("src")) {
        const wrap = node.closest<HTMLElement>(`.${IFRAME_WRAP_CLASS}`);
        const dataSrc = wrap?.getAttribute("data-src");
        if (dataSrc) {
          node.setAttribute("src", dataSrc);
        }
      }
    });
};

const blankIframe = (wrap: HTMLElement, iframe: HTMLIFrameElement) => {
  if (!originalWrapStyles.has(wrap)) {
    originalWrapStyles.set(wrap, wrap.getAttribute("style"));
  }
  if (!originalIframeStyles.has(iframe)) {
    originalIframeStyles.set(iframe, iframe.getAttribute("style"));
  }

  wrap.style.removeProperty("aspect-ratio");
  wrap.style.removeProperty("height");
  iframe.style.visibility = "hidden";
  iframe.style.display = "none";
  // Browsers render iframes in their own layer that can defeat z-index and
  // visibility tricks. Removing the src forces about:blank so there is
  // nothing to render through the overlay.
  iframe.removeAttribute("src");
};

export const useReadonlyIframeStatus = (
  containerRef: RefObject<HTMLDivElement>,
  content: string | undefined
): ReadonlyOverlayEntry[] => {
  const {role} = useSelector(selectUser);
  const isOrganiser = role === Roles.ORGANISER;
  // Anonymous viewers (no role / no session) — e.g. the public email-review
  // page — have no other iframe governance (`useFilterUnverifiedIframes` only
  // runs for EXHIBITOR, this hook only ran for ORGANISER). Enable the same
  // approved-domain gate for them so an APPROVED iframe actually renders
  // instead of staying a blank / src-less frame. `POST /verified-domain/check`
  // is public, so the check needs no session. Exhibitors are handled by the
  // other hook, so they stay excluded here.
  const isAnonymous = !role;
  const enabled = isOrganiser || isAnonymous;

  const [domains, setDomains] = useState<string[]>([]);
  const [entries, setEntries] = useState<ReadonlyOverlayEntry[]>([]);
  const hasIframes = domains.length > 0;
  // `getMy` requires an organiser session — only fetch it for organisers;
  // anonymous viewers rely purely on the public check result.
  const [myDomains] = useMyVerifiedDomains(isOrganiser && hasIframes);
  const {result: checkResult, isReady} = useCheckVerifiedDomains(domains, {
    enabled,
  });

  useEffect(() => {
    if (!enabled || !containerRef.current) {
      setDomains([]);
      return;
    }
    const found = new Set<string>();
    containerRef.current
      .querySelectorAll<HTMLElement>(`.${IFRAME_WRAP_CLASS}`)
      .forEach((wrap) => {
        const src = (wrap.getAttribute("data-src") || "").trim();
        if (src) found.add(src);
        const origin = extractOrigin(src);
        if (origin) found.add(origin);
      });

    setDomains(Array.from(found));
  }, [containerRef, content, enabled]);

  useEffect(() => {
    if (!enabled || !containerRef.current) {
      setEntries([]);
      return;
    }
    if (!isReady) return;
    const wrapNodes = containerRef.current.querySelectorAll<HTMLElement>(
      `.${IFRAME_WRAP_CLASS}`
    );
    if (wrapNodes.length > 0 && domains.length === 0) return;
    restoreIframeVisibility(containerRef.current);
    const next: ReadonlyOverlayEntry[] = [];
    wrapNodes.forEach((wrap) => {
      const iframe = wrap.querySelector<HTMLIFrameElement>(
        `iframe.${IFRAME_CLASS}`
      );
      if (!iframe) return;
      const src = (wrap.getAttribute("data-src") || "").trim();
      const origin = extractOrigin(src);
      const srcStatus = src ? checkResult.get(src.toLowerCase()) : undefined;
      const originStatus = origin
        ? checkResult.get(origin.toLowerCase())
        : undefined;

      if (
        srcStatus === CheckVerifiedDomainStatus.APPROVED ||
        originStatus === CheckVerifiedDomainStatus.APPROVED
      ) {
        return;
      }
      const ownRecord = myDomains.find((d) => {
        const u = (d.url || "").toLowerCase();
        return (
          u === src.toLowerCase() ||
          (origin && u === origin.toLowerCase()) ||
          extractOrigin(d.url) === origin
        );
      });
      if (ownRecord?.isApproved === true) return;
      const status: ReadonlyOverlayStatus =
        srcStatus === CheckVerifiedDomainStatus.REJECTED ||
        originStatus === CheckVerifiedDomainStatus.REJECTED ||
        ownRecord?.isApproved === false
          ? CheckVerifiedDomainStatus.REJECTED
          : CheckVerifiedDomainStatus.PENDING;
      blankIframe(wrap, iframe);
      next.push({wrap, status});
    });
    setEntries(next);
  }, [
    containerRef,
    content,
    checkResult,
    isReady,
    myDomains,
    enabled,
    domains,
  ]);

  useEffect(() => {
    const container = containerRef.current;

    return () => {
      if (container) {
        restoreIframeVisibility(container);
      }
    };
  }, [containerRef]);

  return entries;
};
